/** "Email to mentee" (D14): documents multiselect, recipient shown before sending. */
import React, { useEffect, useState } from 'react';
import { Pressable, Text, View } from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';

import { reviewDesk } from '@/design-system';
import { useTranslation } from '@/hooks/useTranslation';
import type { DeliverableId, EngagementSessionRow } from '@/types/mentorEngagements';
import { ActionButton, mentorStyles } from './shared';
import { WorkspaceModal } from './WorkspaceModal';
import { checkboxA11y } from '@/utils/checkboxA11y';

interface EmailDeliveryModalProps {
  visible: boolean;
  recipient?: string | null;
  sessions: EngagementSessionRow[];
  onClose: () => void;
  onSend: (documents: DeliverableId[]) => Promise<void>;
}

export function EmailDeliveryModal({ visible, recipient, sessions, onClose, onSend }: EmailDeliveryModalProps) {
  const { t } = useTranslation();
  const [selected, setSelected] = useState<DeliverableId[]>(['crf']);
  const [error, setError] = useState<string | null>(null);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!visible) return;
    setSelected(['crf']);
    setError(null);
  }, [visible]);

  const toggle = (id: DeliverableId) => {
    setSelected((current) => (
      current.includes(id) ? current.filter((item) => item !== id) : [...current, id]
    ));
  };

  const send = async () => {
    if (sending) return;
    if (selected.length === 0) {
      setError(t('mentorEngagements.email.selectOne'));
      return;
    }
    setSending(true);
    setError(null);
    try {
      await onSend(selected);
      onClose();
    } catch {
      setError(t('mentorEngagements.email.error'));
    } finally {
      setSending(false);
    }
  };

  const options: { id: DeliverableId; label: string }[] = [
    { id: 'crf', label: t('mentorEngagements.email.crf') },
    ...sessions
      .filter((session) => session.sof_status)
      .map((session) => ({
        id: `sof:${session.id}`,
        label: t('mentorEngagements.email.sofFor', { number: session.session_number }),
      })),
  ];

  return (
    <WorkspaceModal
      testID="email_delivery_modal"
      visible={visible}
      busy={sending}
      title={t('mentorEngagements.email.title')}
      onClose={onClose}
      footer={(
        <>
          <ActionButton label={t('mentorEngagements.email.cancel')} onPress={onClose} disabled={sending} />
          <ActionButton
            testID="email_delivery_send"
            label={t('mentorEngagements.email.send')}
            onPress={send}
            variant="primary"
            disabled={sending || !recipient}
          />
        </>
      )}
    >
      <View style={{ gap: 4 }}>
        <Text style={mentorStyles.fieldLabel}>{t('mentorEngagements.email.recipient')}</Text>
        <Text style={recipient ? mentorStyles.bodyText : mentorStyles.errorText}>
          {recipient || t('mentorEngagements.email.noRecipient')}
        </Text>
      </View>

      <View style={{ gap: 6 }}>
        <Text style={mentorStyles.fieldLabel}>{t('mentorEngagements.email.documents')}</Text>
        {options.map((option) => {
          const checked = selected.includes(option.id);
          return (
            <Pressable
              key={option.id}
              testID={`email_document_${option.id}`}
              onPress={() => toggle(option.id)}
              disabled={sending}
              {...checkboxA11y(checked, sending)}
              style={({ pressed }) => [mentorStyles.row, pressed && mentorStyles.pressed]}
            >
              <MaterialIcons
                name={checked ? 'check-box' : 'check-box-outline-blank'}
                size={20}
                color={checked ? reviewDesk.accent : reviewDesk.muted}
              />
              <Text style={mentorStyles.bodyText}>{option.label}</Text>
            </Pressable>
          );
        })}
      </View>

      {error ? <Text style={mentorStyles.errorText}>{error}</Text> : null}
    </WorkspaceModal>
  );
}

export default EmailDeliveryModal;
